import { type DragEvent as ReactDragEvent, useCallback, useRef, useState } from "react";
import { dragEventHasFiles, filterAcceptedFiles } from "@/lib/file-drag";

export interface FileDropTargetProps {
  onDragEnter: (event: ReactDragEvent<HTMLElement>) => void;
  onDragOver: (event: ReactDragEvent<HTMLElement>) => void;
  onDragLeave: (event: ReactDragEvent<HTMLElement>) => void;
  onDrop: (event: ReactDragEvent<HTMLElement>) => void;
}

/**
 * Turns an element into a file drop target. Tracks dragenter/dragleave with a depth counter (so a
 * drag crossing child boundaries doesn't flicker the state), exposes `isActive` while a file drag
 * is over the element, and forwards the accepted files on drop. Spread `dropTargetProps` on the
 * container element that should accept drops — e.g. an entire chat area, not just the composer.
 *
 * `reset` exists for the one case the depth counter cannot see: a nested drop surface that calls
 * `stopPropagation` on its own drop (both dropzones do, so a file dropped on the visible surface
 * uploads once, not twice). That drop never reaches this container, so its enters are never
 * balanced by anything and the highlight would stay lit. Call `reset` from whatever the nested
 * surface hands the files to — it is idempotent, so the file picker calling it too is free.
 */
export function useFileDropTarget({
  enabled,
  accept,
  onFiles,
}: {
  enabled: boolean;
  accept?: string;
  onFiles: (files: File[]) => void;
}): { isActive: boolean; dropTargetProps: FileDropTargetProps; reset: () => void } {
  const [isActive, setIsActive] = useState(false);
  const depthRef = useRef(0);

  const reset = useCallback(() => {
    depthRef.current = 0;
    setIsActive(false);
  }, []);

  const onDragEnter = useCallback(
    (event: ReactDragEvent<HTMLElement>) => {
      if (!enabled || !dragEventHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      depthRef.current += 1;
      setIsActive(true);
    },
    [enabled]
  );

  const onDragOver = useCallback(
    (event: ReactDragEvent<HTMLElement>) => {
      if (!enabled || !dragEventHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
    },
    [enabled]
  );

  const onDragLeave = useCallback(
    (event: ReactDragEvent<HTMLElement>) => {
      if (!enabled || !dragEventHasFiles(event.dataTransfer)) return;
      depthRef.current = Math.max(0, depthRef.current - 1);
      if (depthRef.current === 0) {
        setIsActive(false);
      }
    },
    [enabled]
  );

  const onDrop = useCallback(
    (event: ReactDragEvent<HTMLElement>) => {
      if (!enabled || !dragEventHasFiles(event.dataTransfer)) return;
      event.preventDefault();
      reset();
      const files = filterAcceptedFiles(Array.from(event.dataTransfer.files ?? []), accept);
      if (files.length > 0) {
        onFiles(files);
      }
    },
    [accept, enabled, onFiles, reset]
  );

  return {
    isActive: enabled && isActive,
    dropTargetProps: { onDragEnter, onDragOver, onDragLeave, onDrop },
    reset,
  };
}
